import React, { useRef } from 'react';
import classNames from 'classnames';
import { flowRight, isEqual } from 'lodash';
import { IHostProps, useTranslation } from '@wix/yoshi-flow-editor';
import {
  IS_COVER_IMAGE_ENABLED_PATH,
  IS_COVER_IMAGE_ENABLED_MOBILE_PATH,
  RELATED_POSTS_LAYOUT_PARAMS,
  RELATED_POSTS_LAYOUT_PARAMS_MOBILE,
  getLayoutName,
  SECTION_RELATED_POSTS,
} from '@wix/communities-blog-client-common';
import EmptyStates from '../../../common/components/empty-states';
import PostListProGallery from '../../../common/components/post-list-pro-gallery';
import { connect } from '../../../common/components/runtime-context';
import withDeviceType, {
  WithDeviceType,
} from '../../../common/hoc/with-device-type';
import { getAppSettingsValue } from '../../../common/selectors/app-settings-base-selectors';
import {
  getUseMobileDisplaySettings,
  getUseMobileLayoutSettings,
} from '../../../common/selectors/app-settings-selectors';
import {
  getShouldUsePostListMobileSliderArrowColor,
  getShouldUsePostListMobileSliderBorderStyles,
} from '../../../common/selectors/mobile-settings-selectors';
import { getRelatedPosts } from '../../../common/selectors/post-selectors';
import { getIsEntityLoading } from '../../../common/store/is-loading/is-loading-selectors';
import { Actions, AppState } from '../../../common/types';
import { RELATED_POSTS_ENTITY } from '../../controller/widget-data';
import {
  getRelatedPostCount,
  getRelatedPostsLayoutType,
} from '../../selectors/related-posts-selectors';

type RuntimeProps = ReturnType<typeof mapRuntimeToProps>;

type OwnProps = {};

type Props = RuntimeProps & OwnProps;

const PostList = ({
  allPosts,
  isLoading,
  componentId,
  layoutType,
  limit,
  layoutName,
  layoutOptions,
  hideCoverImage,
  shouldUsePostListMobileSliderArrowColor,
  shouldUsePostListMobileSliderBorderStyles,
  sliderArrowsPositions,
}: Props) => {
  const { t } = useTranslation();
  const layoutOptionsRef = useRef<Props['layoutOptions']>();
  const hasPosts = Boolean(allPosts.length);

  if (!isLoading && !hasPosts) {
    return (
      <EmptyStates
        title={t('no-posts-feed.nothing-to-show')}
        content={t('no-posts.publish-posts-to-see-them')}
      />
    );
  }

  if (!isEqual(layoutOptionsRef.current, layoutOptions)) {
    layoutOptionsRef.current = layoutOptions;
  }
  const postListRootClassNames = classNames({
    'mobile-slider-arrow-color': shouldUsePostListMobileSliderArrowColor,
    'mobile-slider-border-styles': shouldUsePostListMobileSliderBorderStyles,
  });

  const posts = limit ? allPosts.slice(0, limit) : allPosts;

  return (
    <div className={postListRootClassNames}>
      <PostListProGallery
        className={
          layoutOptions.arrowsPosition === sliderArrowsPositions.ON_GALLERY
            ? 'arrows-on-gallery'
            : ''
        }
        layoutName={layoutName}
        layoutType={layoutType}
        entityCount={posts.length}
        domId={componentId}
        allPosts={posts}
        currentPagePosts={posts}
        isLoading={isLoading}
        showCreatePostAction={false}
        section={SECTION_RELATED_POSTS}
        canSeeMoreButton={canSeeMoreButton}
        layoutOptions={layoutOptionsRef.current}
        hideCoverImage={hideCoverImage}
      />
    </div>
  );
};

function canSeeMoreButton() {
  return false;
}

const mapRuntimeToProps = (
  state: AppState,
  { isMobile }: WithDeviceType,
  _actions: Actions,
  host: IHostProps,
) => {
  const layoutType = getRelatedPostsLayoutType({ state, isMobile });

  const layoutName = getLayoutName(layoutType);
  const {
    sliderShowArrows,
    sliderArrowsSize,
    sliderAutoSlide,
    sliderPauseTime,
    sliderArrowsPosition,
    sliderArrowsColor,
    sliderLoop,
  } = getUseMobileLayoutSettings(state)
    ? RELATED_POSTS_LAYOUT_PARAMS_MOBILE
    : RELATED_POSTS_LAYOUT_PARAMS;

  return {
    allPosts: getRelatedPosts(state),
    limit: getRelatedPostCount(state),
    layoutType,
    layoutName,
    isLoading: getIsEntityLoading(state, RELATED_POSTS_ENTITY),
    componentId: host.id,
    sliderArrowsPositions: sliderArrowsPosition.values,
    hideCoverImage: !getAppSettingsValue({
      state,
      key: getUseMobileDisplaySettings(state)
        ? IS_COVER_IMAGE_ENABLED_MOBILE_PATH
        : IS_COVER_IMAGE_ENABLED_PATH,
      fallback: true,
    }),
    layoutOptions: {
      showArrows: getAppSettingsValue({
        state,
        key: sliderShowArrows.appSettingsPath,
        fallback: sliderShowArrows.defaultValue,
      }),
      arrowsSize: getAppSettingsValue({
        state,
        key: sliderArrowsSize.appSettingsPath,
        fallback: sliderArrowsSize.defaultValue,
      }),
      autoSlide: getAppSettingsValue({
        state,
        key: sliderAutoSlide.appSettingsPath,
        fallback: sliderAutoSlide.defaultValue,
      }),
      pauseTime: getAppSettingsValue({
        state,
        key: sliderPauseTime.appSettingsPath,
        fallback: sliderPauseTime.defaultValue,
      }),
      arrowsPosition: getAppSettingsValue({
        state,
        key: sliderArrowsPosition.appSettingsPath,
        fallback: sliderArrowsPosition.defaultValue,
      }),
      arrowsColor: getAppSettingsValue({
        state,
        key: sliderArrowsColor.appSettingsPath,
        fallback: sliderArrowsColor.defaultValue,
      }),
      loop: getAppSettingsValue({
        state,
        key: sliderLoop.appSettingsPath,
        fallback: sliderLoop.defaultValue,
      }),
    },
    shouldUsePostListMobileSliderArrowColor:
      getShouldUsePostListMobileSliderArrowColor(state),
    shouldUsePostListMobileSliderBorderStyles:
      getShouldUsePostListMobileSliderBorderStyles(state),
  };
};

const connector = connect(mapRuntimeToProps);

export default flowRight(
  withDeviceType,
  connector,
)(PostList) as React.FC<OwnProps>;
